import { useEffect, useRef, useId } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface CallNotificationModalProps {
  open: boolean;
  title?: string;
  eventId?: string;
  natureOfCall?: string;
  location?: string;
  theme?: string;
  variant?: 'assigned' | 'panic';
  viewButtonText?: string;
  dismissButtonText?: string;
  onViewCall: () => void;
  onDismiss: () => void;
}

export function CallNotificationModal({
  open,
  title = '',
  eventId = '',
  natureOfCall = '',
  location = '',
  theme: themeProp,
  variant = 'assigned',
  viewButtonText = 'View Call',
  dismissButtonText = 'OK',
  onViewCall,
  onDismiss,
}: CallNotificationModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;
  const viewBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Scroll lock and focus management
  useEffect(() => {
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
      // Focus the view button when modal opens
      requestAnimationFrame(() => {
        viewBtnRef.current?.focus();
      });
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open]);

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}`}>
      <div
        className={`kstc-dialog kstc-call-notification${variant === 'panic' ? ' kstc-call-notification--panic' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="kstc-call-notification__header kstc-call-notification__header--animated">
          <h2 id={titleId} className="kstc-call-notification__title">{title}</h2>
        </div>
        <div className="kstc-call-notification__body">
          <div className="kstc-call-notification__field">
            <span className="kstc-call-notification__label">Event</span>
            <span className="kstc-call-notification__event-id">{eventId}</span>
          </div>
          <div className="kstc-call-notification__field">
            <span className="kstc-call-notification__label">Nature</span>
            <span className="kstc-call-notification__nature">{natureOfCall}</span>
          </div>
          <div className="kstc-call-notification__location">
            <span className="kstc-call-notification__label">Location</span>
            <span className="kstc-call-notification__location-text">{location}</span>
          </div>
        </div>
        <div className="kstc-call-notification__actions">
          <button
            ref={viewBtnRef}
            className="kstc-call-notification__view-btn"
            onClick={onViewCall}
            type="button"
          >
            {viewButtonText}
          </button>
          <button
            className="kstc-call-notification__dismiss-btn"
            onClick={onDismiss}
            type="button"
          >
            {dismissButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).CallNotificationModalReact = CallNotificationModal;
